// components/RoiInvestorBenefits.tsx
"use client";

import * as React from "react";
import {
    Box,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import Heading from "../heading";
import CtaButton from "../buttonWithArrow";
import Image from "next/image";

export default function RoiInvestorBenefits({ data, onClick, bgcolor }) {
    const borderColor = "#b1b1b1";

    const { title, subtitle, button, image, rows } = data;

    return (
        <Box sx={{ position: "relative", py: { xs: 3, md: 5 }, px: { xs: 1.5, md: 0 }, bgcolor: bgcolor ?? "#ffffff", }}>
            {/* Heading */}
            <Heading title={title} subtitle={subtitle} />

            {/* Outer wrapper (for CTA alignment) */}
            <Box sx={{ position: "relative", maxWidth: { sm: 560, md: 670, lg: 1100 }, mx: "auto" }}>
                {/* Inner frame that ONLY contains the table and its adornments */}
                <Box sx={{ position: "relative", width: "100%", display: "block" }}>
                    {/* Table card */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            overflow: "hidden",
                            borderRadius: "28px",
                            boxShadow: "none",
                            border: `1px solid ${borderColor}`,
                            bgcolor: "#fff",
                        }}
                    >
                        <Table
                            sx={{
                                minWidth: 360,
                                tableLayout: "fixed",
                                "& th, & td": {
                                    p: { xs: 1, sm: 2, md: "12px", lg: 3 },
                                    borderBottom: `1px solid ${borderColor}`,
                                    verticalAlign: "middle",
                                },
                                "& tbody tr:last-of-type td": { borderBottom: "none" },
                            }}
                        >
                            <TableHead>
                                <TableRow
                                    sx={{
                                        "& th": {
                                            fontWeight: 700,
                                            fontSize: { xs: 12, sm: 15, md: 18 },
                                            color: "#111",
                                        },
                                    }}
                                >
                                    <TableCell width="48%">Item</TableCell>
                                    <TableCell
                                        width="26%"
                                        align="center"
                                        sx={{
                                            borderLeft: `1px solid ${borderColor}`,
                                            borderRight: `1px solid ${borderColor}`,
                                        }}
                                    >
                                        Monthly ROI Range (AED)
                                    </TableCell>
                                    <TableCell width="26%" align="center">
                                        Annual ROI Potential
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: 12, sm: 14, md: 18 },
                                                    color: "#111",
                                                    whiteSpace: "pre-line",
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {r.item}
                                            </Typography>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                borderLeft: `1px solid ${borderColor}`,
                                                borderRight: `1px solid ${borderColor}`,
                                                fontSize: { xs: 12, sm: 14, md: 20 },
                                                fontWeight: 600,
                                                color: "#111",
                                            }}
                                        >
                                            {r.monthly}
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontSize: { xs: 12, sm: 14, md: 20 },
                                                fontWeight: 600,
                                                color: "#111",
                                            }}
                                        >
                                            {r.annual}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Side notches (half-circles) */}
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            left: { xs: -9, md: -10 },                 // ≈ -width/2 so the seam sits on the table edge
                            top: { xs: 56, sm: 72, md: 64 },
                            width: { xs: 18, md: 20 },
                            height: { xs: 18, md: 20 },
                            borderRadius: "50%",
                            bgcolor: bgcolor ? bgcolor : "#fff",                            // page bg color so it looks "cut out"
                            border: `1px solid ${borderColor}`,
                            zIndex: 2,
                            clipPath: "inset(0 0 0 50%)",               // keep the RIGHT half only (left notch)
                            pointerEvents: "none",
                        }}
                    />

                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            right: { xs: -9, md: -10 },
                            top: { xs: 56, sm: 72, md: 64 },
                            width: { xs: 18, md: 20 },
                            height: { xs: 18, md: 20 },
                            borderRadius: "50%",
                            bgcolor: bgcolor ? bgcolor : "#fff",
                            border: `1px solid ${borderColor}`,
                            zIndex: 2,
                            clipPath: "inset(0 50% 0 0)",               // keep the LEFT half only (right notch)
                            pointerEvents: "none",
                        }}
                    />


                    {/* Bottom-right flourish (relative to the table frame) */}
                    {/* <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            right: { xs: -28, sm: -100, md: -125, lg: -175 },   // overlap slightly outside
                            bottom: { xs: -10, sm: -18, md: -80, lg: -30 },  // sit on bottom edge
                            width: { xs: 130, md: 160, lg: 210 },
                            height: { xs: 60, md: 65, lg: 95 },
                            pointerEvents: "none",
                            zIndex: 2,                     // under CTA
                            display: { xs: "none", sm: "block" }, // <- uncomment to hide on very small screens
                        }}
                    >
                        <Image
                            src={"/img/investments/travel/sound.svg"}
                            alt=""
                            role="presentation"
                            fill
                            // sizes="(max-width: 600px) 130px, (max-width: 900px) 170px, 210px"
                            style={{ objectFit: "contain", display: "block" }}
                        />
                    </Box> */}
                </Box>

                {/* CTA under the table */}
                {
                    button && <Stack
                        alignItems="center"
                        sx={{ mt: { xs: 4, md: 5 }, position: "relative", zIndex: 3 }}
                    >
                        <CtaButton label={button?.text} onClick={() => onClick()} />
                        {/* <CtaButton cta={{ label: button?.text, href: button?.link }} /> */}
                    </Stack>
                }

            </Box>
        </Box>
    );
}
